import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, adminCompanyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import {
  LogOut, Users, UserCheck, UserX, Clock,
  Gift, DollarSign, Loader2, ChevronRight,
  Building2, FileText, RefreshCw, ShieldCheck
} from 'lucide-react';

function StatCard({ label, value, icon: Icon, colorClass, bgClass, onClick }) {
  return (
    <div onClick={onClick}
      className={`rounded-2xl border p-5 flex items-center justify-between gap-4
        ${bgClass} ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : ''}`}>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide opacity-70 mb-1 ${colorClass}`}>
          {label}
        </p>
        <p className={`text-3xl font-black ${colorClass}`}>{value}</p>
        {onClick && (
          <p className={`text-xs font-semibold mt-1 flex items-center gap-1 opacity-60 ${colorClass}`}>
            View all <ChevronRight className="h-3 w-3" />
          </p>
        )}
      </div>
      <Icon className={`h-10 w-10 opacity-25 ${colorClass}`} />
    </div>
  );
}

function ActionBtn({ icon: Icon, label, sublabel, color, onClick }) {
  const colors = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800 hover:bg-yellow-100',
    green:  'bg-green-50  border-green-200  text-green-800  hover:bg-green-100',
    blue:   'bg-blue-50   border-blue-200   text-blue-800   hover:bg-blue-100',
    purple: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100',
    orange: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100',
  };
  return (
    <button onClick={onClick}
      className={`flex items-center gap-4 w-full border rounded-xl p-4 transition text-left ${colors[color]}`}>
      <Icon className="h-7 w-7 shrink-0" />
      <div className="flex-1">
        <p className="font-bold text-sm">{label}</p>
        {sublabel && <p className="text-xs opacity-60 mt-0.5">{sublabel}</p>}
      </div>
      <ChevronRight className="h-4 w-4 opacity-40 shrink-0" />
    </button>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('staff');
  const [stats, setStats] = useState(null);
  const [companyStats, setCompanyStats] = useState({ total: 0, pending: 0, pendingHire: 0 });
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [staffRes, allCompaniesRes, pendingCompaniesRes, hireRes] = await Promise.allSettled([
        adminAPI.getDashboardStats(),
        adminCompanyAPI.getCompanies(null, 0, 1),
        adminCompanyAPI.getCompanies('PENDING', 0, 1),
        adminCompanyAPI.getHireRequests('PENDING', 0, 1),
      ]);
      if (staffRes.status === 'fulfilled') setStats(staffRes.value.data.data);
      setCompanyStats({
        total:       allCompaniesRes.status === 'fulfilled' ? (allCompaniesRes.value.data.data?.totalElements || 0) : 0,
        pending:     pendingCompaniesRes.status === 'fulfilled' ? (pendingCompaniesRes.value.data.data?.totalElements || 0) : 0,
        pendingHire: hireRes.status === 'fulfilled' ? (hireRes.value.data.data?.totalElements || 0) : 0,
      });
      setLastRefreshed(new Date());
    } catch {
      toast.error('Failed to load some stats');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { key: 'staff',   label: 'Staff',     icon: Users },
    { key: 'company', label: 'Companies', icon: Building2 },
    { key: 'finance', label: 'Finance',   icon: DollarSign },
  ];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-10 w-10 animate-spin text-teal-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">

      {/* NAVBAR */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-8 w-auto" />
            <span className="px-2.5 py-0.5 bg-red-100 text-red-700 rounded-lg text-xs font-bold uppercase tracking-wide">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-700 leading-tight">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-400">Administrator</p>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* HERO STRIP */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl
          px-8 py-5 mb-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-teal-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">Admin Control Panel</h1>
              <p className="text-slate-500 text-xs mt-0.5">
                Last refreshed {lastRefreshed.toLocaleTimeString('en-IN')}
              </p>
            </div>
          </div>
          <button onClick={loadAll}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20
              border border-white/20 text-white text-sm font-semibold
              px-4 py-2 rounded-xl transition">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {/* TAB BAR */}
        <div className="flex gap-1 bg-white border border-gray-100 rounded-2xl p-1.5 shadow-sm mb-8 w-fit">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all
                ${activeTab === key
                  ? 'bg-slate-900 text-white shadow'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}>
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── TAB: STAFF ──────────────────────────────────────── */}
        {activeTab === 'staff' && (
          <div className="space-y-8">
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Staff Overview
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Staff"      value={stats?.totalStaff || 0}
                  icon={Users}     colorClass="text-blue-700"   bgClass="bg-blue-50 border-blue-100" />
                <StatCard label="Pending Approval" value={stats?.pendingApproval || 0}
                  icon={Clock}     colorClass="text-yellow-700" bgClass="bg-yellow-50 border-yellow-100"
                  onClick={() => navigate('/admin/pending-staff')} />
                <StatCard label="Active Staff"     value={stats?.activeStaff || 0}
                  icon={UserCheck} colorClass="text-green-700"  bgClass="bg-green-50 border-green-100" />
                <StatCard label="Rejected"         value={stats?.rejectedStaff || 0}
                  icon={UserX}     colorClass="text-red-700"    bgClass="bg-red-50 border-red-100" />
              </div>
            </section>
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <ActionBtn icon={Clock} color="yellow" label="Approve Registrations"
                  sublabel={`${stats?.pendingApproval || 0} awaiting review`}
                  onClick={() => navigate('/admin/pending-staff')} />
                <ActionBtn icon={UserCheck} color="green" label="All Staff"
                  sublabel={`${stats?.totalStaff || 0} registered`}
                  onClick={() => navigate('/admin/all-staff')} />
                <ActionBtn icon={FileText} color="blue" label="Active Placements"
                  sublabel="View all currently hired staff"
                  onClick={() => navigate('/admin/placements/active')} />
              </div>
            </section>
          </div>
        )}

        {/* ── TAB: COMPANIES ──────────────────────────────────── */}
        {activeTab === 'company' && (
          <div className="space-y-8">
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Organization Overview
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard label="Total Organizations" value={companyStats.total}
                  icon={Building2} colorClass="text-blue-700"   bgClass="bg-blue-50 border-blue-100" />
                <StatCard label="Pending Approval"    value={companyStats.pending}
                  icon={Clock}     colorClass="text-yellow-700" bgClass="bg-yellow-50 border-yellow-100"
                  onClick={() => navigate('/admin/pending-companies')} />
                <StatCard label="Pending Hire Requests" value={companyStats.pendingHire}
                  icon={FileText}  colorClass="text-purple-700" bgClass="bg-purple-50 border-purple-100"
                  onClick={() => navigate('/admin/hire-requests')} />
              </div>
            </section>
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <ActionBtn icon={Building2} color="blue" label="Approve Companies"
                  sublabel={`${companyStats.pending} pending`}
                  onClick={() => navigate('/admin/pending-companies')} />
                <ActionBtn icon={Building2} color="green" label="All Companies"
                  sublabel={`${companyStats.total} registered`}
                  onClick={() => navigate('/admin/all-companies')} />
                <ActionBtn icon={FileText} color="purple" label="Manage Hire Requests"
                  sublabel={`${companyStats.pendingHire} pending`}
                  onClick={() => navigate('/admin/hire-requests')} />
              </div>
            </section>
          </div>
        )}

        {/* ── TAB: FINANCE ────────────────────────────────────── */}
        {activeTab === 'finance' && (
          <div className="space-y-8">
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Financial Overview
              </h2>
              <div className="grid grid-cols-2 gap-4 max-w-xl">
                <StatCard label="Pending Referrals"  value={stats?.pendingReferrals || 0}
                  icon={Gift}       colorClass="text-purple-700" bgClass="bg-purple-50 border-purple-100"
                  onClick={() => navigate('/admin/pending-referrals')} />
                <StatCard label="Pending Incentives" value={stats?.pendingIncentives || 0}
                  icon={DollarSign} colorClass="text-orange-700" bgClass="bg-orange-50 border-orange-100"
                  onClick={() => navigate('/admin/pending-incentives')} />
              </div>
            </section>
            <section>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <ActionBtn icon={Gift} color="purple" label="Approve Referrals"
                  sublabel={`${stats?.pendingReferrals || 0} pending`}
                  onClick={() => navigate('/admin/pending-referrals')} />
                <ActionBtn icon={DollarSign} color="orange" label="Pay Incentives"
                  sublabel={`${stats?.pendingIncentives || 0} pending`}
                  onClick={() => navigate('/admin/pending-incentives')} />
              </div>
            </section>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 max-w-2xl">
              <p className="font-semibold mb-1">💡 Payment Note</p>
              <p className="opacity-75 leading-relaxed">
                Incentive payouts are processed manually. After transferring via UPI or bank transfer,
                mark each incentive as paid and enter the transaction reference number for records.
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
